import React from 'react';
import {
	fetchEmployment,
	useApplyVacancy,
	useCreateAutoApplicationSetting,
	useFetchEmployment,
	useUpdateAutoApplicationSetting,
	type ApplyVacancyResponse,
	type FetchEmploymentResponse,
	type Vacancy,
} from '@rocket-ui/lk-api';
import {isAxiosError} from 'axios';
import {useQueryClient, type QueryClient} from 'react-query';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from 'app/router/routes';
import {Drawer} from 'shared/ui/Drawer';
import {
	formatOfferAutoApplyAddress,
	formatOfferAutoApplyPayment,
	shouldOfferAutoApplyPrompt,
	type OfferAutoApplyVacancy,
} from '../../model/offerAutoApply';
import {
	formatOfferEmploymentRestriction,
	getOfferEmploymentMismatchMessage,
	OFFER_EMPLOYMENT_MISMATCH_API_ERROR,
} from '../../model/offerEmploymentMatch';
import {EnableAutoReplyDrawerBody} from './EnableAutoReplyDrawerBody';
import {OfferConfirmationModal} from './OfferConfirmationModal';
import styles from './EnableAutoReplyDrawer.module.css';

const EMPLOYMENT_QUERY_KEY = ['employment'] as const;
const EMPLOYMENT_SNAPSHOT_FRESH_MS = 60_000;

type ApplyError = {
	message: string;
	employmentMismatch: boolean;
};

type Step = 'closed' | 'applySuccess' | 'appliedOnlySuccess' | 'settings' | 'enabledSuccess';

type ApplyTarget = (Vacancy | ApplyVacancyResponse) & OfferAutoApplyVacancy & {id?: number};

function getApplyErrorMessage(error: unknown): string {
	if (isAxiosError<{errors?: string[]; message?: string; detail?: string}>(error)) {
		return (
			error.response?.data?.errors?.[0] ||
			error.response?.data?.message ||
			error.response?.data?.detail ||
			error.message
		);
	}

	return error instanceof Error ? error.message : 'Не удалось откликнуться';
}

async function loadEmployment(queryClient: QueryClient): Promise<FetchEmploymentResponse | undefined> {
	const state = queryClient.getQueryState<FetchEmploymentResponse>(EMPLOYMENT_QUERY_KEY);
	const snapshot = state?.data;
	const isFresh = snapshot != null && Date.now() - (state?.dataUpdatedAt ?? 0) < EMPLOYMENT_SNAPSHOT_FRESH_MS;

	if (snapshot && isFresh) return snapshot;

	try {
		const {data} = await fetchEmployment();
		queryClient.setQueryData(EMPLOYMENT_QUERY_KEY, data);
		return data;
	} catch {
		return queryClient.getQueryData<FetchEmploymentResponse>(EMPLOYMENT_QUERY_KEY) ?? snapshot;
	}
}

export function useOfferApplyAutoReplyFlow() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	useFetchEmployment();
	const {mutateAsync: apply, isLoading: isApplying} = useApplyVacancy();
	const {mutateAsync: createSetting, isLoading: isCreating} = useCreateAutoApplicationSetting();
	const {mutateAsync: updateSetting, isLoading: isUpdating} = useUpdateAutoApplicationSetting();

	const [step, setStep] = React.useState<Step>('closed');
	const [context, setContext] = React.useState<ApplyTarget | null>(null);
	const [applyError, setApplyError] = React.useState<ApplyError | null>(null);

	const close = () => {
		setStep('closed');
		setContext(null);
	};

	/**
	 * Click "Откликнуться":
	 * - checkbox on (skip auto-apply) → apply + simple «Готово!» modal
	 * - no upsell (checkbox hidden) → apply + simple «Готово!» modal
	 * - upsell → apply + «Готово!» with auto-apply CTA
	 *   - "В другой раз" → close (отклик уже отправлен)
	 *   - "Откликаться автоматически" → settings → enable auto-reply
	 */
	const startFlow = async (vacancy: ApplyTarget, options?: {skipAutoApply?: boolean}) => {
		if (vacancy.id == null) return;

		setApplyError(null);

		const employmentKinds = vacancy.agreementKind?.employmentKinds;
		const mismatchMessage = getOfferEmploymentMismatchMessage(employmentKinds, await loadEmployment(queryClient));

		if (mismatchMessage) {
			setApplyError({message: mismatchMessage, employmentMismatch: true});
			return;
		}

		try {
			const applied = await apply({id: vacancy.id});
			const next = {...vacancy, ...applied};

			setContext(next);

			if (options?.skipAutoApply || !shouldOfferAutoApplyPrompt(vacancy)) {
				setStep('appliedOnlySuccess');
				return;
			}

			setStep('applySuccess');
		} catch (error) {
			const message = getApplyErrorMessage(error);
			const isApiMismatch = message.trim() === OFFER_EMPLOYMENT_MISMATCH_API_ERROR;

			setApplyError({
				message: isApiMismatch ? formatOfferEmploymentRestriction(employmentKinds) ?? message : message,
				employmentMismatch: isApiMismatch,
			});
		}
	};

	const enableAutoReply = async () => {
		if (!context?.customer?.id || context.id == null) return;

		const amountCents = context.reward?.amountCents;
		const currency = context.reward?.currency ?? 'RUB';
		const body = {
			customerId: context.customer.id,
			minAmount:
				amountCents != null
					? {
							amountCents,
							currency,
						}
					: undefined,
			remote: context.remote ?? false,
			locations: context.locations?.map((location) => ({
				id: location.id,
				name: location.name,
			})),
			enabled: true,
		};

		try {
			// Отклик уже отправлен в startFlow — тут только настройки автоотклика.
			const settingId = context.executorAutoApplicationSetting?.id;
			if (settingId != null) {
				await updateSetting({id: settingId, body});
			} else {
				await createSetting(body);
			}

			setStep('enabledSuccess');
		} catch (error) {
			setApplyError({message: getApplyErrorMessage(error), employmentMismatch: false});
			close();
		}
	};

	const paymentLabel = formatOfferAutoApplyPayment(context?.reward?.amountCents);
	const addressLabel = formatOfferAutoApplyAddress(context ?? {});
	const isBusy = isApplying || isCreating || isUpdating;

	const flow = (
		<>
			<OfferConfirmationModal
				isOpen={step === 'applySuccess'}
				title="Готово!"
				description={
					<>
						<p style={{margin: 0}}>Вы откликнулись на это предложение.</p>
						<p style={{margin: '8px 0 0'}}>
							Хотите автоматически откликаться на подходящие предложения от этого заказчика?
						</p>
					</>
				}
				confirmText="Откликаться автоматически"
				cancelText="В другой раз"
				onConfirm={() => setStep('settings')}
				onCancel={close}
			/>

			<OfferConfirmationModal
				isOpen={step === 'appliedOnlySuccess'}
				title="Готово!"
				description="Вы откликнулись на это предложение"
				confirmText="Продолжить"
				onConfirm={close}
				onCancel={close}
			/>

			<Drawer
				open={step === 'settings'}
				onClose={close}
				title={<span className={styles.drawerTitle}>Включить автоотклик</span>}
				showCloseButton
				closeButtonPosition="end"
				bodyClassName={styles.drawerBody}
				footerClassName={styles.drawerFooter}
				footerContent={
					<div className={styles.footer}>
						<button
							type="button"
							className={styles.enableButton}
							disabled={isBusy}
							onClick={() => {
								void enableAutoReply();
							}}
						>
							Включить автоотклик
						</button>
					</div>
				}
			>
				<EnableAutoReplyDrawerBody paymentLabel={paymentLabel} addressLabel={addressLabel} />
			</Drawer>

			<OfferConfirmationModal
				isOpen={step === 'enabledSuccess'}
				title="Готово!"
				description="Вы будете автоматически откликаться на подходящие предложения от этого заказчика"
				confirmText="Продолжить"
				onConfirm={close}
				onCancel={close}
			/>

			{applyError ? (
				<OfferConfirmationModal
					isOpen
					title="Не удалось откликнуться"
					description={applyError.message}
					confirmText={applyError.employmentMismatch ? 'Подключить статус' : 'Ок'}
					showIllustration={false}
					onConfirm={() => {
						const openStatuses = applyError.employmentMismatch;
						setApplyError(null);
						if (openStatuses) navigate(ROUTES.profileStatuses);
					}}
					onCancel={() => setApplyError(null)}
				/>
			) : null}
		</>
	);

	return {
		applyWithFlow: startFlow,
		isApplying: isBusy,
		flow,
	};
}
